import { useMemo } from 'react';
import { COMPONENT_POSITION, INVISIBLES } from '@workspace/constants';
import { getBackgroundClass, openDocsBot, shouldOpenInNewTab } from '@workspace/lib';
import { mapHeadingVariant, processHeading } from '@workspace/lib/heading-utils';
import { ProductSummaryBlock, CTAItem } from './product-summary.types';
import { typographyVariants } from '@workspace/ui/common/Typography/typography.variants';
import { isEasycraftProductSummary, resolveProductSummaryLayoutStyle } from './product-summary-layout';

export function useProductSummary(block: ProductSummaryBlock) {
  const {
    isActive = true,
    backgroundColor,
    headline,
    description,
    image,
    imagePosition = 'left',
    ctaUnderHeadline,
    ctaUnderDescription,
    isVideo = false,
    video,
    layoutStyle,
  } = block;

  const safeCtaUnderHeadline = Array.isArray(ctaUnderHeadline) ? ctaUnderHeadline : [];
  const safeCtaUnderDescription = Array.isArray(ctaUnderDescription) ? ctaUnderDescription : [];

  const isEasycraft = isEasycraftProductSummary(block._type);
  const resolvedLayoutStyle = useMemo(
    () => resolveProductSummaryLayoutStyle(block._type, layoutStyle),
    [block._type, layoutStyle],
  );
  const resolvedCtaUnderHeadline = isEasycraft ? [] : safeCtaUnderHeadline;
  const resolvedCtaUnderDescription = isEasycraft
    ? safeCtaUnderDescription.filter((item) => item?.type !== 'askGreg')
    : safeCtaUnderDescription;
  const resolvedIsVideo = isEasycraft ? false : isVideo;
  const resolvedVideo = isEasycraft ? undefined : video;

  const resolvedHeading = useMemo(() => {
    const { text: resolvedHeadingText } = processHeading(headline);
    const headingVariant = mapHeadingVariant(headline, 'h2');
    const headingFontClass = typographyVariants({ variant: headingVariant });
    return { resolvedHeadingText, headingVariant, headingFontClass };
  }, [headline]);

  const cleanImagePosition = useMemo(() => {
    return typeof imagePosition === 'string' ? imagePosition.replace(INVISIBLES, '').trim().toLowerCase() : 'left';
  }, [imagePosition]);

  const backgroundClass = useMemo(() => getBackgroundClass(backgroundColor), [backgroundColor]);

  const hasMedia = useMemo(
    () => !!(resolvedIsVideo && resolvedVideo) || !!(image?.asset?._ref || image?.url),
    [resolvedIsVideo, resolvedVideo, image],
  );

  const imageOrderClass = useMemo(
    () => (cleanImagePosition === COMPONENT_POSITION.POSITION_RIGHT ? 'md:order-3' : 'order-1'),
    [cleanImagePosition],
  );

  const contentOrderClass = useMemo(
    () => (cleanImagePosition === COMPONENT_POSITION.POSITION_RIGHT ? 'md:order-1' : 'order-3'),
    [cleanImagePosition],
  );

  const handleAskGregClick = () => {
    openDocsBot();
  };

  const getOpenInNewTab = (item: CTAItem): boolean => {
    return shouldOpenInNewTab({
      type: item.linkType,
      isOpenInNewTab: item.openInNewTab,
    });
  };

  return {
    isActive,
    resolvedHeading,
    cleanImagePosition,
    backgroundClass,
    hasMedia,
    imageOrderClass,
    contentOrderClass,
    handleAskGregClick,
    getOpenInNewTab,
    description,
    ctaUnderHeadline: resolvedCtaUnderHeadline,
    ctaUnderDescription: resolvedCtaUnderDescription,
    isVideo: resolvedIsVideo,
    video: resolvedVideo,
    image,
    headline,
    layoutStyle: resolvedLayoutStyle,
  };
}
